import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { AlertController, LoadingController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';

import { from, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, finalize, map } from 'rxjs/operators';

import { PropertyForceApiService } from '../../misc/services/propertyForceApi.service';
import { AuthenticateUserResponse } from '../../typings/property-force-api';
import * as fromDataProviderActions from '../actions/data-provider.actions';
import * as fromLoggerActions from '../actions/logger.actions';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromSettingActions from '../actions/setting.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromDataProviderSelectors from '../selectors/data-provider.selectors';
import * as fromDeviceSelectors from '../selectors/device.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';

@Injectable()
export class DataProviderEffects {
  private actions$ = inject(Actions);
  private alertController = inject(AlertController);
  private loadingController = inject(LoadingController);
  private location = inject(Location);
  private propertyForceApiService = inject(PropertyForceApiService);
  private router = inject(Router);
  private store = inject(Store);

  updateLoginAccountReferralCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromDataProviderActions.updateDataProviderAuthentication),
      withLatestFrom(this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))),
      switchMap(
        ([
          {
            payload: { code, data, password, username }
          },
          deviceData
        ]) =>
          this.propertyForceApiService
            .getData<AuthenticateUserResponse>('/authenticateUser', {
              deviceData,
              office: code,
              password,
              username
            })
            .pipe(
              switchMap((response) => {
                let actions: Action[];

                if (response.d.StatusCode.includes('ERROR')) {
                  actions = [
                    fromDataProviderActions.authenticateDataProviderFailure({
                      payload: { error: new Error(response.d.Status) }
                    })
                  ];
                } else if (response.d.StatusCode.includes('TERMS')) {
                  actions = [
                    fromDataProviderActions.authenticateDataProviderFailure({
                      payload: {
                        error: new Error('Please agree to the updated terms of the data provider.')
                      }
                    })
                  ];
                } else if (response.d.StatusCode.includes('EXPIRED')) {
                  actions = [
                    fromDataProviderActions.authenticateDataProviderPasswordExpired({
                      payload: {
                        isOnboardingFlow: false,
                        username: username ?? ''
                      }
                    })
                  ];
                } else {
                  actions = [
                    fromUserActions.updateMobileUser({
                      payload: {
                        providerUsername: username,
                        securityCode: code
                      }
                    }),
                    fromLoggerActions.updateConfiguration({
                      payload: {
                        appName: data.organization.applicationName,
                        securityCode: code
                      }
                    }),
                    fromSettingActions.updateReferralCodeSuccess({ payload: { code, data } }),
                    fromDataProviderActions.authenticateDataProviderSuccess({
                      payload: {
                        data: {
                          password,
                          username
                        },
                        isOnboardingFlow: false
                      }
                    })
                  ];
                }
                return actions;
              }),

              catchError((error: HttpErrorResponse) =>
                of(
                  fromDataProviderActions.authenticateDataProviderFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  authenticateDataProvider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromDataProviderActions.authenticateDataProvider),
      withLatestFrom(
        this.store.pipe(select(fromSettingSelectors.selectReferralCode)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      switchMap(
        ([
          {
            payload: { data, isOnboardingFlow }
          },
          referralCode,
          deviceData
        ]) =>
          this.propertyForceApiService
            .getData<AuthenticateUserResponse>('/authenticateUser', {
              deviceData,
              office: referralCode,
              password: data.password,
              username: data.username
            })
            .pipe(
              map((response) => {
                let action: Action;
                if (response.d.StatusCode.includes('ERROR')) {
                  action = fromDataProviderActions.authenticateDataProviderFailure({
                    payload: { error: new Error(response.d.Status) }
                  });
                } else if (response.d.StatusCode.includes('TERMS')) {
                  action = fromDataProviderActions.authenticateDataProviderFailure({
                    payload: { error: new Error('Please agree to the updated terms of the data provider.') }
                  });
                } else if (response.d.StatusCode.includes('EXPIRED')) {
                  action = fromDataProviderActions.authenticateDataProviderPasswordExpired({
                    payload: {
                      isOnboardingFlow,
                      username: data.username
                    }
                  });
                } else {
                  action = fromDataProviderActions.authenticateDataProviderSuccess({
                    payload: {
                      data: {
                        password: data.password,
                        username: data.username
                      },
                      isOnboardingFlow: isOnboardingFlow
                    }
                  });
                }
                return action;
              }),

              catchError((error: HttpErrorResponse) =>
                of(
                  fromDataProviderActions.authenticateDataProviderFailure({
                    payload: { error }
                  })
                )
              )
            )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromDataProviderActions.changePassword),
      withLatestFrom(
        this.store.pipe(select(fromDataProviderSelectors.selectUsername)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      switchMap(
        ([
          {
            payload: { isOnboardingFlow, newPassword, oldPassword }
          },
          user,
          deviceData
        ]) =>
          this.propertyForceApiService
            .getData(
              '/changePassword',
              {
                deviceData,
                newPassword,
                oldPassword,
                user
              },
              {
                shouldConvertStatusCodeErrors: true
              }
            )
            .pipe(
              concatMap(() => {
                const actions: Action[] = [
                  fromDataProviderActions.changePasswordSuccess({
                    payload: { newPassword: newPassword ? btoa(newPassword) : null }
                  })
                ];
                if (!isOnboardingFlow) {
                  actions.push(
                    fromDataProviderActions.authenticateDataProvider({
                      payload: {
                        data: {
                          password: newPassword ? btoa(newPassword) : '',
                          username: user ?? ''
                        },
                        isOnboardingFlow
                      }
                    })
                  );
                }
                return actions;
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  fromDataProviderActions.changePasswordFailure({
                    payload: {
                      error
                    }
                  })
                )
              )
            )
      )
    )
  );

  authenticateDataProviderPasswordExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDataProviderActions.authenticateDataProviderPasswordExpired),
        concatMap(({ payload: { isOnboardingFlow } }) =>
          isOnboardingFlow
            ? this.router.navigate(['/onboarding', 'change-password'])
            : this.router.navigate(['/more', 'login-account', 'change-password'])
        )
      ),
    {
      dispatch: false
    }
  );

  authenticateUserCredentialsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDataProviderActions.authenticateDataProviderSuccess),
        filter(({ payload: { isOnboardingFlow } }) => isOnboardingFlow),
        concatMap(() => this.router.navigate(['/onboarding', 'change-icon']))
      ),
    {
      dispatch: false
    }
  );

  changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDataProviderActions.changePasswordSuccess),
        map(async () => {
          this.location.back();
          const alert = await this.alertController.create({
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Success',
            message: 'Your password has been changed.'
          });
          await alert.present();
          await alert.onDidDismiss();
        })
      ),
    {
      dispatch: false
    }
  );

  changePasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromDataProviderActions.changePasswordFailure),
        map(async ({ payload: { error } }) => {
          const alert = await this.alertController.create({
            buttons: [
              {
                role: 'cancel',
                text: 'Ok'
              }
            ],
            header: 'Oops',
            message: `Your password could not be changed: ${error.message}. Please try again.`
          });
          await alert.present();
          await alert.onDidDismiss();
        })
      ),
    {
      dispatch: false
    }
  );

  migratePersistedOldStateUserDataSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromPersistedStateActions.migratePersistedOldStateUserDataSuccess),
      withLatestFrom(
        this.store.pipe(select(fromDataProviderSelectors.selectIsBlackKnight)),
        this.store.pipe(select(fromDeviceSelectors.selectPropertyForceDeviceData))
      ),
      concatMap(([{ payload }, dataProviderRequiresAuthentication, deviceData]) =>
        from(
          this.loadingController.create({
            backdropDismiss: false,
            message: 'Migrating provider credentials...',
            showBackdrop: true,
            translucent: true
          })
        ).pipe(
          concatMap(async (loader) => {
            await loader.present();
            return loader;
          }),
          switchMap((loader) =>
            dataProviderRequiresAuthentication
              ? this.propertyForceApiService
                  .getData<AuthenticateUserResponse>('/authenticateUser', {
                    deviceData,
                    office: payload.data.referralCode,
                    password: payload.state.authData.password,
                    username: payload.state.authData.username
                  })
                  .pipe(
                    map((data) =>
                      data.d.StatusCode.includes('ERROR')
                        ? fromPersistedStateActions.migratePersistedOldStateFailure({
                            payload: {
                              error: new Error(
                                `Unable to validate and migrate data provider credentials (${data.d.Status}).`
                              )
                            }
                          })
                        : fromPersistedStateActions.migratePersistedOldStateDataProviderCredentialsSuccess({
                            payload: {
                              data: {
                                password: payload.state.authData.password,
                                username: payload.state.authData.username
                              },
                              state: payload.state
                            }
                          })
                    ),
                    catchError(() =>
                      of(
                        fromPersistedStateActions.migratePersistedOldStateFailure({
                          payload: {
                            error: new Error('Unable to validate and migrate data provider credentials.')
                          }
                        })
                      )
                    ),
                    finalize(async () => await loader.dismiss())
                  )
              : of(
                  fromPersistedStateActions.migratePersistedOldStateDataProviderCredentialsSuccess({
                    payload: {
                      data: {
                        password: null,
                        username: null
                      },
                      state: payload.state
                    }
                  })
                )
          )
        )
      )
    )
  );
}
